import { computed, inject, ref, type ComputedRef, type Directive, type Ref } from 'vue'
import type { ComponentNode, NodeAnimation, NodeAnimationStep } from '@api/types.gen'
import { PipeDisplayMode, usePipe, type PipeInterface } from '@render/composables/usePipe'
import {
  COLLAPSE_TYPES,
  DEFAULT_IN_DURATION,
  DEFAULT_IN_EASING,
  DEFAULT_OUT_DURATION,
  DEFAULT_OUT_EASING,
  DEFAULT_SCALE,
  motionKeyframes,
  slideOffset,
  type BoxMetrics,
} from '@render/composables/nodeAnimationFrames'

/**
 * Enter/leave motion for a rendered node (`ComponentNode.animation`).
 *
 * Two mechanisms, picked by `trigger`:
 *
 * - `mount` / `visible` — a CSS keyframe class (node-animation.css). Chosen so
 *   the animation survives prerendering: it runs at first paint from the static
 *   HTML with no JS involved. These triggers have no leave — nothing removes
 *   the node, and on the `visible` trigger an IntersectionObserver only swaps
 *   the pending class for the animating one.
 * - `rule` — Web Animations driven from `<Transition>` JS hooks. The leave has
 *   to hold the element in the DOM past its `v-if` flip, and the collapse types
 *   need a box measured at the moment the animation starts, neither of which a
 *   stylesheet can do. Running the non-collapse types through the same path
 *   keeps `in` and `out` independently configurable.
 * - `click` — the same Web Animations `run()`, started by a click listener the
 *   directive adds. The node is on screen throughout; each click replays its
 *   entrance.
 */

type Step = NodeAnimationStep
type Trigger = NonNullable<NodeAnimation['trigger']>

const CSS_TRIGGERS: ReadonlySet<Trigger> = new Set(['mount', 'visible'])

/** What a node's `staggerChildren` hands its direct children (RenderComponents' `cascade`). */
export interface AnimationCascade {
  /** `staggerChildren`: ms added per child index. */
  step: number
  /** When the parent's `mount` / `visible` animation starts; 0 for any other parent. */
  delay: number
  /** The parent's `mount` / `visible` animation, for children without one of their own. */
  inherit?: { trigger: Trigger; enter: Step; once: boolean }
}

interface AnimationTarget {
  nodeId: string
  node: ComponentNode
  /** `slotContentKey` of the enclosing User Component placement; `''` outside one. */
  slotContent: ComputedRef<string>
  cascade: AnimationCascade | undefined
  index: number
}

interface TrackedNode {
  target: AnimationTarget
  /** `target.slotContent` when the element last played. */
  playedWith: string
}

interface ResolvedAnimation {
  trigger: Trigger
  once: boolean
  enter: Step
  leave: Step
  /** `enter.delay` plus this node's share of its parent's `staggerChildren`, after the parent's own start. */
  enterDelay: number
}

interface AnimationState {
  /**
   * Rule-driven animations stay off until the pipe reports ready plus a frame.
   * The boot-time localStorage merge lands one tick after mount (usePipe's
   * `loadPersistedData`), so every node ruled on a persisted variable flips
   * false→true just after hydration — without this grace window a page using
   * them plays a flurry of enter animations on every load.
   */
  settled: Ref<boolean>
  /** Nodes whose `repeat: 'once'` animation has already run. Reactive: a `rule` node reads it at render. */
  played: Ref<Record<string, true>>
  /**
   * Class list decided for the currently-mounted instance of a node, keyed by
   * nodeId. Memoised so an unrelated re-render mid-animation cannot strip the
   * class and abort it; dropped by the directive on unmount.
   */
  instanceClasses: Map<string, string[]>
  /** The `in` step last resolved for a node — kept for the console log on `mount` / `visible` reveal, which otherwise has only a nodeId to work with. */
  enterSteps: Map<string, Step>
  observer: IntersectionObserver | null
  observed: Map<Element, string>
  running: WeakMap<Element, Animation>
  /** Mounted elements of animated nodes, for the click listener and the replay on a page change. */
  tracked: Map<Element, TrackedNode>
  /** Whether this pipe's `navigation:show` listener is registered. */
  replaysOnNavigation: boolean
}

// Keyed off the pipe instance rather than module scope: `nuxt generate`
// prerenders many pages in one process, and module-level state would leak
// played/pending bookkeeping from one page's render into the next.
const stateByPipe = new WeakMap<PipeInterface, AnimationState>()

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function getState(pipe: PipeInterface): AnimationState {
  const existing = stateByPipe.get(pipe)
  if (existing) return existing

  const state: AnimationState = {
    settled: ref(typeof window === 'undefined'),
    played: ref({}),
    instanceClasses: new Map(),
    enterSteps: new Map(),
    observer: null,
    observed: new Map(),
    running: new WeakMap(),
    tracked: new Map(),
    replaysOnNavigation: false,
  }
  stateByPipe.set(pipe, state)

  if (typeof window !== 'undefined') {
    void pipe.whenReady().then(() => {
      requestAnimationFrame(() => {
        state.settled.value = true
      })
    })
  }

  return state
}

/** The slot fillers of a User Component placement, as one comparable string. */
function slotContentKey(slots: Record<string, ComponentNode[]>): string {
  return Object.values(slots)
    .flatMap((nodes) => nodes.map((node) => node.nodeId))
    .join(' ')
}

export function useNodeAnimation() {
  const pipe = usePipe()
  const state = getState(pipe)

  const sharedNodeSlots = inject<ComputedRef<Record<string, ComponentNode[]>> | null>(
    'sharedNodeSlots',
    null,
  )
  const slotContent = computed(() => (sharedNodeSlots ? slotContentKey(sharedNodeSlots.value) : ''))

  function animationsOff(): boolean {
    // The editing canvas force-shows and force-hides nodes as the author works;
    // replaying motion on every one of those is noise, not preview. The preview
    // iframe runs in INTERACTIVE_VIEW and does animate. `editorAnimations` is
    // the visibility-rule tester's opt-out of that suppression, so an author
    // flipping a test variable sees what a visitor would.
    if (pipe.disableAllVisibilityRules.value) return true
    return pipe.currentMode.value === PipeDisplayMode.EDITOR && !pipe.editorAnimations.value
  }

  function resolve(
    node: ComponentNode,
    cascade: AnimationCascade | undefined,
    index: number,
  ): ResolvedAnimation | null {
    // Cheapest check first, and deliberately before `animationsOff()`: this runs
    // for every node on every render, and bailing here keeps an unanimated node
    // from taking a reactive dependency on the display mode at all.
    const config = node.animation
    const enter = config?.in
    if (!enter || enter.type === 'none') return null
    if (animationsOff()) return null

    const trigger = config?.trigger ?? 'rule'
    return {
      trigger,
      once: config?.repeat === 'once',
      // A collapse animates a measured box, which only the `rule` trigger's JS
      // hooks can produce. Stored combinations the editor no longer offers
      // degrade to the nearest motion rather than silently doing nothing.
      enter:
        trigger === 'rule' || !COLLAPSE_TYPES.has(enter.type)
          ? enter
          : { ...enter, type: 'fade-slide' },
      leave: config?.out ?? enter,
      enterDelay:
        (cascade && CSS_TRIGGERS.has(trigger) ? cascade.delay : 0) +
        (enter.delay ?? 0) +
        (cascade?.step ?? 0) * index,
    }
  }

  /**
   * The `mount` / `visible` animation a node plays: its own, or else — when it
   * has none or only a `click` one — the one its parent's `staggerChildren`
   * hands down. A `rule` node keeps its own and inherits nothing.
   */
  function resolveEntrance(
    node: ComponentNode,
    cascade: AnimationCascade | undefined,
    index: number,
  ): ResolvedAnimation | null {
    const own = resolve(node, cascade, index)
    if (own && own.trigger !== 'click') return CSS_TRIGGERS.has(own.trigger) ? own : null

    if (!cascade?.inherit || animationsOff()) return null
    return {
      ...cascade.inherit,
      leave: cascade.inherit.enter,
      enterDelay: cascade.delay + cascade.step * index,
    }
  }

  /** The `cascade` RenderComponents passes to the children of `node`. */
  function cascadeFor(
    node: ComponentNode,
    cascade: AnimationCascade | undefined,
    index: number,
  ): AnimationCascade | undefined {
    const step = node.animation?.staggerChildren
    if (!step) return undefined

    const entrance = resolveEntrance(node, cascade, index)
    if (!entrance) return { step, delay: 0 }
    const { trigger, enter, once } = entrance
    return { step, delay: entrance.enterDelay, inherit: { trigger, enter, once } }
  }

  /** Inline custom properties the CSS keyframes read. Rendered server-side too. */
  function stylesFor(node: ComponentNode, cascade: AnimationCascade | undefined, index: number) {
    const cfg = resolveEntrance(node, cascade, index)
    if (!cfg) return undefined

    const { enter } = cfg
    const vars: Record<string, string> = {
      '--pa-kf': `pa-kf-${enter.type}`,
      '--pa-in-duration': `${enter.duration ?? DEFAULT_IN_DURATION}ms`,
      '--pa-in-delay': `${cfg.enterDelay}ms`,
      '--pa-in-easing': enter.easing ?? DEFAULT_IN_EASING,
    }
    if (enter.type === 'fade-slide') {
      const { dx, dy } = slideOffset(enter)
      vars['--pa-in-dx'] = `${dx}px`
      vars['--pa-in-dy'] = `${dy}px`
    }
    if (enter.type === 'scale') vars['--pa-in-scale'] = String(DEFAULT_SCALE)
    const itemStep = itemStagger(node)
    if (itemStep) vars['--pa-item-step'] = `${itemStep}ms`
    return vars
  }

  /** `staggerChildren` of a node with no child nodes — applied to its own `pa-item`s, if it marks any. */
  function itemStagger(node: ComponentNode): number {
    return node.children?.length ? 0 : (node.animation?.staggerChildren ?? 0)
  }

  function classesFor(node: ComponentNode, cascade: AnimationCascade | undefined, index: number) {
    const cfg = resolveEntrance(node, cascade, index)
    if (!cfg) return undefined

    state.enterSteps.set(node.nodeId, cfg.enter)

    const cached = state.instanceClasses.get(node.nodeId)
    if (cached) return cached

    const spent = cfg.once && state.played.value[node.nodeId]
    const classes = spent ? [] : [cfg.trigger === 'visible' ? 'pa-pending' : 'pa-anim']
    if (classes.length && itemStagger(node)) classes.push('pa-stagger')
    state.instanceClasses.set(node.nodeId, classes)
    return classes
  }

  /** So an author testing a built pipe (see the animation URL in AnimationDialog) can confirm a trigger actually fired. */
  function logPlayback(
    nodeId: string,
    trigger: string,
    direction: 'in' | 'out',
    step?: Step,
  ): void {
    if (!step) return

    console.log(
      `[animation] ${direction === 'in' ? '\u25b6' : '\u25c0'} ${nodeId} \u2014 ${step.type} (trigger: ${trigger}, ${direction})`,
    )
  }

  // ─── rule trigger: Web Animations through <Transition> hooks ───────────────

  /** The only DOM read in the collapse path; the arithmetic on it is in nodeAnimationFrames.ts. */
  function readBoxMetrics(el: HTMLElement): BoxMetrics {
    const cs = getComputedStyle(el)
    return {
      outerHeight: el.getBoundingClientRect().height,
      boxSizing: cs.boxSizing,
      paddingTop: cs.paddingTop,
      paddingBottom: cs.paddingBottom,
      marginTop: cs.marginTop,
      marginBottom: cs.marginBottom,
      borderTopWidth: cs.borderTopWidth,
      borderBottomWidth: cs.borderBottomWidth,
    }
  }

  function run(
    el: Element,
    step: Step,
    direction: 'in' | 'out',
    delay: number,
    done: () => void,
  ): void {
    const node = el as HTMLElement
    const frames = prefersReducedMotion() ? null : motionKeyframes(step, () => readBoxMetrics(node))
    if (!frames) {
      done()
      return
    }

    const isCollapse = COLLAPSE_TYPES.has(step.type)
    const previousOverflow = isCollapse ? node.style.overflow : null
    if (isCollapse) node.style.overflow = 'hidden'
    if (direction === 'out') node.style.pointerEvents = 'none'

    const animation = node.animate(direction === 'in' ? frames : [...frames].reverse(), {
      duration: step.duration ?? (direction === 'in' ? DEFAULT_IN_DURATION : DEFAULT_OUT_DURATION),
      delay: direction === 'in' ? delay : (step.delay ?? 0),
      easing: step.easing ?? (direction === 'in' ? DEFAULT_IN_EASING : DEFAULT_OUT_EASING),
      // `backwards` holds the entering from-state through the delay; `forwards`
      // keeps a leaving element at its end state until Vue detaches it, which is
      // one frame later — without it the element flashes back to full size.
      fill: direction === 'in' ? 'backwards' : 'forwards',
    })
    state.running.set(node, animation)

    const finish = () => {
      if (state.running.get(node) === animation) state.running.delete(node)
      if (isCollapse) node.style.overflow = previousOverflow ?? ''
      if (direction === 'out') node.style.pointerEvents = ''
      done()
    }
    // `finished` rejects when the animation is cancelled (an interrupted
    // enter/leave); the cancel hooks below do the cleanup in that case.
    animation.finished.then(finish, () => {})
  }

  function cancel(el: Element): void {
    const animation = state.running.get(el)
    if (!animation) return
    state.running.delete(el)
    animation.cancel()
    const node = el as HTMLElement
    node.style.overflow = ''
    node.style.pointerEvents = ''
  }

  /** The `pa-item`s `playIn` staggers instead of `el`, when `node` has an item stagger. */
  function itemsOf(el: Element, node: ComponentNode): Element[] {
    if (!itemStagger(node) || !el.classList.contains('pa-items')) return []
    return Array.from(el.querySelectorAll(':scope > .pa-item'))
  }

  /** The enter on `el`, or on each of its `pa-item`s one after another. */
  function playIn(el: Element, node: ComponentNode, step: Step, delay: number, done: () => void) {
    const items = itemsOf(el, node)
    if (!items.length) {
      run(el, step, 'in', delay, done)
      return
    }
    const gap = itemStagger(node)
    items.forEach((item, i) => {
      cancel(item)
      run(item, step, 'in', delay + gap * i, i === items.length - 1 ? done : () => {})
    })
  }

  function cancelIn(el: Element, node: ComponentNode): void {
    cancel(el)
    for (const item of itemsOf(el, node)) cancel(item)
  }

  function markPlayed(nodeId: string): void {
    if (state.played.value[nodeId]) return
    state.played.value = { ...state.played.value, [nodeId]: true }
  }

  /**
   * Props for the `<Transition>` wrapping a node. Always returns `css: false`,
   * so a node with no animation gets no transition classes and Vue resolves the
   * leave synchronously — identical to having no `<Transition>` at all.
   */
  function transitionFor(
    node: ComponentNode,
    cascade: AnimationCascade | undefined,
    index: number,
  ) {
    const cfg = resolve(node, cascade, index)
    const inert = { css: false } as const
    if (!cfg || cfg.trigger !== 'rule') return inert
    if (!state.settled.value) return inert
    if (cfg.once && state.played.value[node.nodeId]) return inert

    return {
      css: false,
      // Covers the node revealed by an *ancestor's* rule rather than its own —
      // it mounts already-shown, so there is never an enter to hook and the
      // animation would silently do nothing. AnimationDialog offers exactly
      // that configuration ("follows the visibility rule on X"), and it is the
      // only way to animate inside a teleporting StickyContainer. The `settled`
      // gate above is what keeps this from firing on the boot-time pass.
      appear: true,
      onEnter: (el: Element, done: () => void) => {
        logPlayback(node.nodeId, 'rule', 'in', cfg.enter)
        playIn(el, node, cfg.enter, cfg.enterDelay, done)
      },
      onLeave: (el: Element, done: () => void) => {
        logPlayback(node.nodeId, 'rule', 'out', cfg.leave)
        run(el, cfg.leave, 'out', 0, done)
      },
      onAfterEnter: () => {
        if (cfg.once) markPlayed(node.nodeId)
      },
      onEnterCancelled: (el: Element) => cancelIn(el, node),
      onLeaveCancelled: cancel,
    }
  }

  // ─── visible trigger: one shared observer ─────────────────────────────────

  function reveal(el: Element): void {
    const nodeId = state.observed.get(el)
    if (!nodeId) return
    state.observer?.unobserve(el)
    state.observed.delete(el)

    logPlayback(nodeId, 'visible', 'in', state.enterSteps.get(nodeId))

    const classes = state.instanceClasses.get(nodeId)
    if (classes) {
      // Mutated in place AND applied to the DOM: the in-place edit keeps a later
      // re-render emitting the same class list, the classList call makes the
      // swap happen now rather than at the next unrelated patch.
      const pending = classes.indexOf('pa-pending')
      if (pending !== -1) classes[pending] = 'pa-anim'
    }
    el.classList.remove('pa-pending')
    el.classList.add('pa-anim')
    markPlayed(nodeId)
  }

  function observe(el: Element, nodeId: string): void {
    if (typeof IntersectionObserver === 'undefined') {
      reveal(el)
      return
    }
    if (!state.observer) {
      state.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) if (entry.isIntersecting) reveal(entry.target)
        },
        { rootMargin: '0px 0px -10% 0px' },
      )
    }
    state.observed.set(el, nodeId)
    state.observer.observe(el)
  }

  // ─── click trigger ─────────────────────────────────────────────────────────

  /**
   * Bound to `pointerup` and `click`, the way CTAButton activates: a CTA cancels
   * the touch's `click`, so a pointer press counts on `pointerup` and `click`
   * only for the keyboard (`detail === 0`).
   */
  function playOnClick(event: Event): void {
    const isPointer = event.type === 'pointerup'
    if (isPointer ? (event as PointerEvent).button !== 0 : (event as MouseEvent).detail !== 0)
      return

    const el = event.currentTarget as Element
    const target = state.tracked.get(el)?.target
    const cfg = target && resolve(target.node, undefined, 0)
    if (!target || !cfg || cfg.trigger !== 'click') return
    const playedKey = `${target.nodeId}:click`
    if (cfg.once && state.played.value[playedKey]) return

    cancelIn(el, target.node)
    logPlayback(target.nodeId, 'click', 'in', cfg.enter)
    playIn(el, target.node, cfg.enter, cfg.enterDelay, () => {})
    if (cfg.once) markPlayed(playedKey)
  }

  function listenForClicks(el: Element, listen: boolean): void {
    for (const type of ['pointerup', 'click']) {
      if (listen) el.addEventListener(type, playOnClick)
      else el.removeEventListener(type, playOnClick)
    }
  }

  function track(el: Element, target: AnimationTarget | null): void {
    if (!target) {
      state.tracked.delete(el)
      listenForClicks(el, false)
      return
    }
    const tracked = state.tracked.get(el)
    if (tracked) tracked.target = target
    else state.tracked.set(el, { target, playedWith: target.slotContent.value })

    listenForClicks(el, target.node.animation?.trigger === 'click')
  }

  // ─── page change: a kept User Component wrapping new content ───────────────

  /**
   * Replays the `mount` / `visible` animation of every element that stayed
   * mounted across a page change while the slot content of its User Component
   * placement changed (see node-animations.md).
   */
  function replayForNewSlotContent(): void {
    const restart: Element[] = []
    for (const [el, tracked] of state.tracked) {
      const { nodeId, node, slotContent: current, cascade, index } = tracked.target
      if (current.value === tracked.playedWith) continue
      tracked.playedWith = current.value

      const cfg = resolveEntrance(node, cascade, index)
      if (!cfg) continue
      if (cfg.once && state.played.value[nodeId]) continue
      if (!el.classList.contains('pa-anim')) continue

      if (cfg.trigger === 'visible') {
        el.classList.replace('pa-anim', 'pa-pending')
        observe(el, nodeId)
      } else {
        logPlayback(nodeId, 'mount', 'in', cfg.enter)
        restart.push(el)
      }
    }
    if (!restart.length) return

    for (const el of restart) el.classList.remove('pa-anim')
    restart[0]!.getBoundingClientRect()
    for (const el of restart) el.classList.add('pa-anim')
  }

  if (!state.replaysOnNavigation) {
    state.replaysOnNavigation = true
    pipe.subscribe('navigation:show', replayForNewSlotContent)
  }

  /**
   * Owns everything that must not happen during render: marking a `mount`
   * animation spent, starting the observer, and dropping the memoised class
   * list when the node goes away. Also registers the `previewAnimation`
   * editor command for every animated node (any trigger) so the "Preview"
   * button in AnimationDialog can replay it on demand, bypassing the normal
   * EDITOR-mode `animationsOff()` gate.
   */
  const vNodeAnimation: Directive<Element, AnimationTarget | null> = {
    mounted(el, binding) {
      const target = binding.value
      track(el, target)
      if (!target) return
      const { nodeId, node } = target

      // Always logs once per real mount (never per render, unlike `resolve()`)
      // — so "nothing happened and no log either" is diagnosable: it reports
      // *why* a rule animation is inert (editor mode, not-yet-settled grace
      // window, or already spent by `repeat: once`) even when the enter never
      // actually plays. The success case still gets its own log from
      // `transitionFor`'s `onEnter`.
      const trigger = node.animation?.trigger ?? 'rule'
      if (trigger === 'rule' && node.animation?.in && node.animation.in.type !== 'none') {
        console.log(
          `[animation] ${nodeId} mounted (trigger: rule) — animationsOff=${animationsOff()}, ` +
            `settled=${state.settled.value}, playedOnce=${!!state.played.value[nodeId]}`,
        )
      }

      const classes = state.instanceClasses.get(nodeId)
      if (classes?.length) {
        if (classes.includes('pa-pending')) observe(el, nodeId)
        else {
          markPlayed(nodeId)
          logPlayback(nodeId, 'mount', 'in', state.enterSteps.get(nodeId))
        }
      }

      if (node.animation?.in && node.animation.in.type !== 'none') {
        pipe.registerEditorCommands(nodeId, {
          previewAnimation: () => {
            const current = state.tracked.get(el)?.target.node ?? node
            const enter = current.animation?.in
            if (!enter || enter.type === 'none') return
            logPlayback(nodeId, 'preview', 'in', enter)
            cancelIn(el, current)
            playIn(el, current, enter, 0, () => {})
          },
        })
      }
    },
    updated(el, binding) {
      track(el, binding.value)
    },
    unmounted(el, binding) {
      track(el, null)
      const target = binding.value
      if (!target) return
      const { nodeId } = target
      state.instanceClasses.delete(nodeId)
      state.enterSteps.delete(nodeId)
      if (state.observed.has(el)) {
        state.observer?.unobserve(el)
        state.observed.delete(el)
      }
      pipe.unregisterEditorCommands(nodeId, { previewAnimation: () => {} })
    },
  }

  /**
   * Target for the directive, or null for nodes it has nothing to do for.
   * Deliberately NOT gated by `animationsOff()` — unlike `resolve()`, this
   * must still attach in EDITOR mode so `previewAnimation` can register there
   * (that's exactly where the Preview button lives).
   */
  function directiveTarget(
    node: ComponentNode,
    cascade: AnimationCascade | undefined,
    index: number,
  ): AnimationTarget | null {
    const enter = node.animation?.in
    const animated = (!!enter && enter.type !== 'none') || !!cascade?.inherit
    return animated ? { nodeId: node.nodeId, node, slotContent, cascade, index } : null
  }

  return {
    classesFor,
    stylesFor,
    transitionFor,
    cascadeFor,
    directiveTarget,
    vNodeAnimation,
  }
}
